import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  BehaviorSubject,
  Observable,
  of,
  switchMap,
  shareReplay,
  finalize,
  map,
  tap,
  combineLatest,
  filter,
} from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { v4 as uuid } from 'uuid';

import { DprStatusTrackerGridComponent } from '@dpr-details/components';
import { DprDetailsGridContainerBaseComponent } from '@dpr-details/models/component';
import { DprStatusTrackerGridData } from '@dpr-details/models/grid';
import {
  DprAccumulationService,
  DprDetailsService,
  DprFormulaCalculationService,
  DprStatusTrackerGridAdapterService,
} from '@dpr-details/services';
import { GridSync } from '@grid/models';
import { SelectedSubproject } from '@shared/models';
import {
  DprDetails,
  DprDetailsSync,
  DprStatusTracker,
  DprStatusTrackerDetails,
} from '@shared/models/dpr';
import { StatusTrackerWithData } from '@shared/models/project-setup';
import { DprDetailsSyncModelBuilderService } from '@dpr-details/services/sync';
import { Sync } from '@shared/models/grid';
import { SyncService } from '@shared/services/grid';

@UntilDestroy()
@Component({
  selector: 'app-dpr-status-tracker-grid-container',
  templateUrl: './dpr-status-tracker-grid-container.component.html',
  styleUrls: ['./dpr-status-tracker-grid-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DprStatusTrackerGridContainerComponent
  extends DprDetailsGridContainerBaseComponent<DprStatusTrackerGridData>
  implements OnInit
{
  @ViewChild(DprStatusTrackerGridComponent)
  private gridComponent: DprStatusTrackerGridComponent;

  gridData$ = new BehaviorSubject<DprStatusTrackerGridData[]>(null);
  gridsReady$ = new BehaviorSubject<boolean>(false);
  statusTracker$ = new BehaviorSubject<StatusTrackerWithData>(null);
  colorTypes$ = this.userDetailsFacade.query.colorTypes$;

  gridsData: Map<number, DprStatusTrackerGridData[]>;
  gridsChanges = new Map<number, GridSync<DprStatusTrackerGridData>>();
  prevGridsChanges = new Map<number, GridSync<DprStatusTrackerDetails>>();
  statusTrackerId: number;
  isSaving: boolean;

  constructor(
    protected activatedRoute: ActivatedRoute,
    private dprStatusTrackerGridAdapterService: DprStatusTrackerGridAdapterService,
    private dprDetailsSyncModelBuilderService: DprDetailsSyncModelBuilderService,
    private dprDetailsService: DprDetailsService,
    private syncService: SyncService,
    private dprAccumulationService: DprAccumulationService,
    private dprFormulaCalculationService: DprFormulaCalculationService
  ) {
    super(activatedRoute);

    this.dprDetailsRouteParams = this.activatedRoute.parent.parent.params;
  }

  ngOnInit(): void {
    super.ngOnInit();

    combineLatest([this.selectedSubproject$, this.activatedRoute.params])
      .pipe(
        untilDestroyed(this),
        map(([selectedSubproject, params]) => {
          const statusTracker = selectedSubproject.data.statusTrackers.find(
            (x) => x.id === +params.statusTrackerId
          );

          if (statusTracker) {
            this.statusTracker$.next(statusTracker);
          }
        })
      )
      .subscribe();

    combineLatest([
      this.selectedSubproject$,
      this.dprDetails$,
      this.dprDetailsOfflineChanges$,
    ])
      .pipe(
        untilDestroyed(this),
        tap(([selectedSubproject, dprDetails, dprDetailsOfflineChanges]) => {
          // timeout is needed to execute this after the grid changes are cleared following a successful save
          setTimeout(() => {
            this.gridsData = this.buildStoredGridsData(
              dprDetails,
              selectedSubproject
            );
            this.buildGridsChanges(
              dprDetailsOfflineChanges,
              selectedSubproject
            );
            this.gridsReady$.next(true);
          });
        })
      )
      .subscribe();

    combineLatest([
      this.gridsReady$,
      this.statusTracker$,
      this.dprDetailsOfflineChanges$,
    ])
      .pipe(
        untilDestroyed(this),
        filter(([gridReady, statusTracker]) => !!gridReady && !!statusTracker),
        tap(([_, statusTracker]) => {
          // timeout is needed here to allow the grid API to register before setting the predefined changes
          setTimeout(() => {
            this.onStatusTrackerChanged(
              this.statusTrackerId,
              +statusTracker.id
            );
            this.statusTrackerId = +statusTracker.id;
          });
        })
      )
      .subscribe();
  }

  save(
    _?,
    isAutoSync = false,
    isAutoSyncFromAdminChange = false
  ): Observable<boolean> {
    if (!this.isDirty$.value || this.isSaving) {
      throw new Error(
        'Unable to save data. There are either no changes to be saved or there is already a save in progress'
      );
    }

    this.isSaving = true;

    this.storeGridState(this.statusTrackerId);

    const validateChanges$ = of(
      isAutoSync ||
        isAutoSyncFromAdminChange ||
        this.isAppOnline ||
        this.validateFormulaChanges()
    );

    const saveChanges$ = validateChanges$.pipe(
      switchMap((valid) => {
        if (!valid) {
          return of(false);
        }

        const finalGridChanges = new Map<
          number,
          GridSync<DprStatusTrackerDetails>
        >();

        for (const [key, val] of this.gridsChanges) {
          if (!val || this.syncService.isSyncModelEmpty(val)) {
            continue;
          }

          const currentStatusTracker =
            this.selectedSubproject.data.statusTrackers.find(
              (x) => x.id === key
            );
          const changes =
            this.dprStatusTrackerGridAdapterService.buildSyncDataFromGridData(
              val,
              this.prevGridsChanges.get(key) ??
                this.syncService.getDefaultSyncModel(),
              currentStatusTracker,
              this.dprDetails.dprStatusTracker
            );

          finalGridChanges.set(key, changes);
        }

        return this.saveChanges(finalGridChanges, isAutoSyncFromAdminChange);
      }),
      finalize(() => {
        this.isSaving = false;
      }),
      shareReplay()
    );

    saveChanges$.pipe(untilDestroyed(this)).subscribe();

    return saveChanges$.pipe(map((result) => !!result));
  }

  /**
   * The values go straight onto the row rather than through the grid: a Status
   * Tracker save only sends the cells registered as changed, and a formula's value
   * is derived, not entered. The edited column repaints the row once this returns.
   */
  onFormulaInputEdited(row: DprStatusTrackerGridData): void {
    const formulaContext =
      this.dprAccumulationService.getStatusTrackerFormulaContext(
        this.statusTrackerId
      );

    if (!formulaContext) {
      return;
    }

    this.dprFormulaCalculationService
      .calculateStatusTrackerRows(formulaContext, [row])
      .get(String(row.rowId))
      ?.forEach((value, columnId) => {
        row[formulaContext.columnNamesById.get(columnId)] = value;
      });
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  protected getData(): void {}

  protected refreshData(): void {
    this.isDirty$.next(false);
    this.gridsChanges.clear();
    this.prevGridsChanges.clear();
    this.gridComponent.reset();
  }

  //#region Private methods

  private validateFormulaChanges(): boolean {
    const changedGrids = [...this.gridsChanges].filter(
      ([, changes]) => changes && !this.syncService.isSyncModelEmpty(changes)
    );

    if (!changedGrids.length) {
      return true;
    }

    const storedGridsData = this.buildStoredGridsData(
      this.dprDetails,
      this.selectedSubproject
    );

    return changedGrids.every(([statusTrackerId, changes]) =>
      this.dprStatusTrackerGridAdapterService.validateFormulaChanges(
        changes,
        storedGridsData.get(statusTrackerId) ?? [],
        this.dprAccumulationService.getStatusTrackerFormulaContext(
          statusTrackerId
        ),
        this.selectedSubproject.data.statusTrackers.find(
          (statusTracker) => +statusTracker.id === statusTrackerId
        )
      )
    );
  }

  private saveChanges(
    changesMap: Map<number, GridSync<DprStatusTrackerDetails>>,
    isAutoSyncFromAdminChange: boolean
  ): Observable<DprStatusTracker> {
    let statusTrackerChanges: Sync<DprStatusTrackerDetails> =
      this.syncService.getDefaultSyncModel();

    for (const key of changesMap.keys()) {
      const changes = changesMap.get(key);
      statusTrackerChanges = this.syncService.mergeModels([
        statusTrackerChanges,
        changes,
      ]);
    }

    const syncData = this.dprDetailsSyncModelBuilderService.forDprStatusTracker(
      this.selectedSubproject.id,
      this.dprDetails.dprStatusTracker?.id ?? uuid(),
      this.dprDetails.id,
      statusTrackerChanges
    );

    const saveChanges$ = this.dprDetailsService
      .syncDprStatusTracker(syncData, isAutoSyncFromAdminChange)
      .pipe(shareReplay());

    saveChanges$.pipe(untilDestroyed(this)).subscribe({
      next: (result) => {
        this.onSaveSuccess(result);
      },
      error: (error: HttpErrorResponse) => {
        this.saveErrorHandlerService.onSaveError(
          this.customTabNames.statusTrackerTabName,
          this.selectedSubproject.id,
          error
        );
      },
    });

    return saveChanges$;
  }

  private onSaveSuccess(result: DprStatusTracker): void {
    // A save's answer carries no previous values, so the section keeps the ones it
    // was loaded with, which its carry-overs and running totals are worked out from.
    this.offlineFacade.setDprStatusTracker(
      {
        ...result,
        previousValues:
          result.previousValues ??
          this.dprDetails.dprStatusTracker?.previousValues,
      },
      this.dprDetails.id
    );

    this.refreshData();
  }

  private onStatusTrackerChanged(
    previousValue: number,
    currentValue: number
  ): void {
    if (previousValue == null || currentValue === previousValue) {
      // update grid with the newly selected status tracker type data
      this.gridData$.next(this.gridsData.get(currentValue));

      // set predefined changes for newly selected status tracker type data (needed in case we apply offline changes)
      setTimeout(() => {
        this.gridApi?.setPredefinedChanges(this.gridsChanges.get(currentValue));
      });
      return;
    }

    // store changes of current  status tracker type grid
    this.storeGridState(previousValue);

    // set grid as pristine
    this.gridApi?.reset();

    // initialize the grid according to the selected status tracker
    this.gridComponent.initializeGrid(true);

    // update grid with the newly selected status tracker type data
    if (this.gridsData.get(currentValue)) {
      this.gridData$.next(this.gridsData.get(currentValue));
    }

    // set predefined changes for newly selected status tracker data
    if (this.gridsChanges.get(currentValue)) {
      setTimeout(() => {
        this.gridApi?.setPredefinedChanges(this.gridsChanges.get(currentValue));
      });
    }
  }

  private storeGridState(statusTrackerId: number): void {
    this.gridsChanges.set(statusTrackerId, this.gridApi?.getChanges());
    this.gridsData.set(statusTrackerId, this.gridComponent.gridData);
  }

  private buildStoredGridsData(
    dprDetails: DprDetails,
    selectedSubproject: SelectedSubproject
  ): Map<number, DprStatusTrackerGridData[]> {
    return this.dprStatusTrackerGridAdapterService.buildDprStatusTrackerGridData(
      selectedSubproject.data.statusTrackers,
      dprDetails,
      this.dprAccumulationService.getStatusTrackerAccumulations(),
      this.dprAccumulationService.getStatusTrackerFormulaContexts(),
      this.dprAccumulationService.getStatusTrackerStoredColumnIds()
    );
  }

  private buildGridsChanges(
    dprDetailsOfflineChanges: DprDetailsSync,
    selectedSubproject: SelectedSubproject
  ): void {
    const statusTrackers = selectedSubproject.data.statusTrackers;

    for (const statusTracker of statusTrackers) {
      const offlineChangesDprDetails =
        this.getOfflineChanges(+statusTracker.id, dprDetailsOfflineChanges) ??
        (this.syncService.getDefaultSyncModel() as GridSync<DprStatusTrackerDetails>);

      const offlineChanges =
        this.dprStatusTrackerGridAdapterService.convertDprStatusTrackerDetailsSyncToStatusTrackerGridSync(
          offlineChangesDprDetails,
          statusTracker,
          this.gridsData.get(+statusTracker.id)
        );
      this.gridsChanges.set(+statusTracker.id, offlineChanges);
      this.prevGridsChanges.set(+statusTracker.id, offlineChangesDprDetails);
    }
  }

  private getOfflineChanges(
    statusTrackerId: number,
    dprDetailsOfflineChanges: DprDetailsSync
  ): GridSync<DprStatusTrackerDetails> {
    return {
      addedList: (
        dprDetailsOfflineChanges?.dprStatusTracker?.syncDprStatusTrackerDetails
          .addedList ?? []
      ).filter((x) => x.statusTrackerId === statusTrackerId),
      updatedList: (
        dprDetailsOfflineChanges?.dprStatusTracker?.syncDprStatusTrackerDetails
          .updatedList ?? []
      ).filter((x) => x.statusTrackerId === statusTrackerId),
      deletedIds:
        dprDetailsOfflineChanges?.dprStatusTracker?.syncDprStatusTrackerDetails
          .deletedIds ?? [],
    };
  }

  //#endregion
}
